import React, { useState } from 'react';
import { formatDublinDate } from '../../utils/date';
import { useGetStudentQuery, useUpdateStudentMutation } from '../../store/api/localApi';
import { useToast } from '../Toast';
import { PencilIcon } from '@heroicons/react/24/outline';

interface Props {
  studentId: string;
}

/**
 * Parent-facing Medical & Allergies card on the child-home page.
 *
 * Self-contained edit session -- ParentChildHome has no existing
 * isEditing/edit-form state to join (unlike StudentDetail.tsx's combined
 * edit session), so this component owns startEdit -> controlled inputs ->
 * save/cancel end to end, following the edit-toggle pattern in
 * ui/src/pages/settings/PersonalInfo.tsx (edit button toggles isEditing;
 * Cancel/Save appear only while editing).
 *
 * Backed by useGetStudentQuery (Task 4) rather than the page's existing
 * useGetMyChildrenQuery, because that hook is a deliberately narrow
 * summary endpoint that doesn't return gp/allergies.
 */
export const ChildMedicalSection: React.FC<Props> = ({ studentId }) => {
  const { showError } = useToast();
  const { data: student, isLoading } = useGetStudentQuery(studentId);
  const [updateStudent, { isLoading: isSaving }] = useUpdateStudentMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    gpName: '',
    gpPhone: '',
    gpAddress: '',
    hasAllergies: false,
    allergyDetail: '',
  });

  const startEdit = () => {
    setForm({
      gpName: student?.gp?.name || '',
      gpPhone: student?.gp?.phone || '',
      gpAddress: student?.gp?.address || '',
      hasAllergies: student?.allergies?.hasAny || false,
      allergyDetail: student?.allergies?.detail || '',
    });
    setIsEditing(true);
  };

  const save = async () => {
    // Only enforced when THIS edit session is what leaves the record in an
    // invalid hasAny:true/no-detail state. A pre-existing row can already be
    // hasAny:true with no detail (Task 2's schema permits it, and it's the
    // exact shape `startEdit`'s seed carries forward unchanged if the parent
    // never touches the allergy section this session). Blocking save in that
    // case would make an unrelated edit -- e.g. fixing the GP phone number --
    // permanently unsavable unless the parent invents allergy text they have
    // no information for. So skip the guard when the loaded record was
    // ALREADY in that state and this session hasn't changed hasAllergies or
    // added detail since. Same fix as Task 5's StudentDetail.tsx guard.
    const allergyStateUnchangedFromLoad =
      !!student?.allergies?.hasAny &&
      !student?.allergies?.detail &&
      form.hasAllergies === student.allergies.hasAny &&
      !form.allergyDetail.trim();
    if (form.hasAllergies && !form.allergyDetail.trim() && !allergyStateUnchangedFromLoad) {
      showError('Add allergy details, or turn the allergies toggle off.');
      return;
    }
    try {
      await updateStudent({
        id: studentId,
        updates: {
          gp:
            form.gpName || form.gpPhone || form.gpAddress
              ? {
                  name: form.gpName,
                  phone: form.gpPhone,
                  ...(form.gpAddress ? { address: form.gpAddress } : {}),
                }
              : undefined,
          allergies: {
            hasAny: form.hasAllergies,
            ...(form.allergyDetail ? { detail: form.allergyDetail } : {}),
          },
        },
      }).unwrap();
      setIsEditing(false);
    } catch (err: any) {
      showError(err.data?.error || err.data?.message || 'Failed to save');
    }
  };

  if (isLoading) return null;

  return (
    <div className="bg-white shadow rounded-lg p-6" data-testid="parent-medical-contact-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Medical &amp; Allergies</h3>
        {!isEditing && (
          <button
            data-testid="parent-edit-medical-btn"
            onClick={startEdit}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <PencilIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Family doctor name</label>
              <input
                type="text"
                data-testid="parent-gp-name"
                value={form.gpName}
                onChange={(e) => setForm((p) => ({ ...p, gpName: e.target.value }))}
                className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Family doctor phone</label>
              <input
                type="text"
                data-testid="parent-gp-phone"
                value={form.gpPhone}
                onChange={(e) => setForm((p) => ({ ...p, gpPhone: e.target.value }))}
                className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Family doctor address</label>
            <input
              type="text"
              data-testid="parent-gp-address"
              value={form.gpAddress}
              onChange={(e) => setForm((p) => ({ ...p, gpAddress: e.target.value }))}
              className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              data-testid="parent-allergy-toggle"
              checked={form.hasAllergies}
              onChange={(e) => setForm((p) => ({ ...p, hasAllergies: e.target.checked }))}
            />
            Has allergies
          </label>
          {form.hasAllergies && (
            <textarea
              data-testid="parent-allergy-detail"
              value={form.allergyDetail}
              onChange={(e) => setForm((p) => ({ ...p, allergyDetail: e.target.value }))}
              rows={2}
              placeholder="What are they allergic to, and how severe?"
              className="block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              data-testid="parent-cancel-medical-btn"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              data-testid="parent-save-medical-btn"
              disabled={isSaving}
              onClick={save}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Family doctor</p>
            <p className="text-sm text-gray-900" data-testid="parent-gp-name">
              {student?.gp?.name || <span className="text-gray-400 italic">Not provided</span>}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Phone</p>
            <p className="text-sm text-gray-900" data-testid="parent-gp-phone">
              {student?.gp?.phone ? (
                <a href={`tel:${student.gp.phone}`} className="text-primary-600 hover:underline">
                  {student.gp.phone}
                </a>
              ) : (
                <span className="text-gray-400 italic">Not provided</span>
              )}
            </p>
          </div>
          {student?.gp?.audit && (
            <p className="text-xs text-gray-400" data-testid="parent-gp-audit">
              Updated {formatDublinDate(student.gp.audit.lastUpdatedAt)} by{' '}
              {student.gp.audit.lastUpdatedByRole === 'PARENT'
                ? 'you'
                : student.gp.audit.lastUpdatedByRole}
            </p>
          )}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs uppercase tracking-wide text-gray-400">Allergies</p>
            <p className="text-sm text-gray-900">
              {student?.allergies?.hasAny ? (
                <>
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                    Yes — has allergies
                  </span>
                  {student.allergies.detail && (
                    <span className="block mt-1" data-testid="parent-allergy-detail">
                      {student.allergies.detail}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-400 italic">No known allergies</span>
              )}
            </p>
            {student?.allergies?.audit && (
              <p className="text-xs text-gray-400 mt-1" data-testid="parent-allergy-audit">
                Updated {formatDublinDate(student.allergies.audit.lastUpdatedAt)} by{' '}
                {student.allergies.audit.lastUpdatedByRole === 'PARENT'
                  ? 'you'
                  : student.allergies.audit.lastUpdatedByRole}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
